import { Injectable, NgZone, OnDestroy, inject } from '@angular/core';
import { Observable, Subject, filter, map } from 'rxjs';
import {
  ANDROID_APP_RESUMED_EVENT,
  ANDROID_NETWORK_CHANGED_EVENT,
  REALTIME_PROBE_URL,
  REALTIME_STREAM_URL,
  SSE_CONNECTED_EVENT,
  SSE_PROBE_EVENT,
  SSE_SESSION_EXPIRED_EVENT,
} from '../shared/constants/realtime.constants';
import { LoggerService } from './logger.service';
import {
  getActiveTunnelDataRaw,
  getActiveUserDataRaw,
} from '../shared/utils/impersonation-handoff.util';

export type RealtimeAuthMode = 'user' | 'tunnel' | 'none';

export interface RealtimeEvent<T = any> {
  id?: string;
  type: string;
  data: T;
  raw?: any;
}

/** Emitted on `ready$` each time the stream completes the `event: connected` handshake. */
export interface RealtimeStreamReady {
  mode: RealtimeAuthMode;
  connectionId: string | null;
  /**
   * True when this handshake follows an earlier ready stream in the same
   * `connectUser()`/`connectTunnel()` session — i.e. a liveness-watchdog,
   * session-expiry or network reconnect. The server does not replay events
   * missed while the old stream was dead, so consumers that render a list
   * should reconcile from the API on `reconnect === true`.
   */
  reconnect: boolean;
}

/**
 * Fetch-based SSE client (supports Authorization Bearer).
 * Stream is ready only after the server sends `event: connected`.
 */
@Injectable({ providedIn: 'root' })
export class RealtimeEventsService implements OnDestroy {
  private readonly logger = inject(LoggerService);
  /** fetch() read loop runs outside NgZone; re-enter so modal/HTTP UI updates paint. */
  private readonly ngZone = inject(NgZone);

  private readonly eventSubject = new Subject<RealtimeEvent>();
  readonly events$ = this.eventSubject.asObservable();

  private readonly readySubject = new Subject<RealtimeStreamReady>();
  /** Fires once per successful `event: connected` handshake; see RealtimeStreamReady.reconnect. */
  readonly ready$ = this.readySubject.asObservable();
  /** Whether any stream has been ready since the last connectUser()/connectTunnel(); reset by disconnect(). */
  private hasBeenReady = false;

  private abort: AbortController | null = null;
  private reconnectTimer: ReturnType<typeof setTimeout> | null = null;
  private lastEventId: string | null = null;
  private authMode: RealtimeAuthMode = 'none';
  private intentionalClose = false;
  /** True only after `event: connected` from the server. */
  private connected = false;
  /** HTTP response accepted; waiting for `event: connected`. */
  private streamOpen = false;
  private reconnectAttempt = 0;
  private readonly maxReconnectDelayMs = 30_000;

  /**
   * Liveness contract with the server keepalive. Both realtime-api and
   * device-events-api emit a `: <comment>` keepalive every serverKeepaliveMs
   * (`DEFAULT_KEEPALIVE_MS` in `@car-wash/core`). We treat the stream as dead
   * when we have not seen ANY bytes (keepalive or event) for well over that
   * interval, so a silently-dead socket (laptop sleep, proxy idle reap, network
   * blip that never fires `error`/`online`) is detected and reconnected instead
   * of hanging forever on a zombie `reader.read()`.
   */
  // Mirrors SSE_KEEPALIVE_MS in libs/core/src/config/env.js. Liveness stays at 3 missed ticks;
  // if the server interval changes, this must move with it or the watchdog will either reconnect
  // healthy streams or sit on dead ones.
  private readonly serverKeepaliveMs = 2_000;
  private readonly livenessTimeoutMs = 6_000;
  private readonly watchdogIntervalMs = 2_000;
  /** Epoch ms of the last byte received on the stream (0 = never). */
  private lastActivityAt = 0;
  /** Server-assigned connection id from `event: connected`. */
  private connectionId: string | null = null;
  private watchdogTimer: ReturnType<typeof setInterval> | null = null;

  /**
   * Active liveness probe (CWS-1377). The passive watchdog can only infer death
   * from silence, so it cannot beat `keepalive x missed-tick tolerance` — at the
   * original 10s server keepalive that was 30-40s, which is why operators were
   * reloading the tunnel screen by hand. The probe asks the question directly:
   * POST a nonce, and require it back *on this stream* within `probeTimeoutMs`.
   *
   * Deliberately not a plain health poll: that request would ride a different
   * TCP flow and, under carrier NAT, usually succeed precisely because it opens
   * a fresh one — reporting healthy during the exact failure being hunted.
   *
   * Detection is `time until a tick sees the stream quiet` + `probeTimeoutMs`,
   * so roughly 5s from the last byte, against the passive watchdog's 6-8s. That
   * margin is the whole point of the probe and is guarded by the invariant on
   * probeTimeoutMs — check it before changing any of these numbers.
   *
   * With the server keepalive at 2s the quiet threshold almost always applies,
   * so a healthy stream issues very few probes. Note the passive watchdog now
   * covers 6-8s on its own, so the probe's margin over it is smaller than it
   * was at a 10s keepalive — its remaining value is that it is *evidence*
   * rather than inference, and it degrades safely: a probe that cannot be
   * delivered is abandoned, never mistaken for a dead stream.
   */
  private readonly probeIntervalMs = 2_000;
  /**
   * INVARIANT: probeQuietThresholdMs + probeTimeoutMs < livenessTimeoutMs.
   *
   * Otherwise the passive watchdog reaches the same verdict first and the probe
   * is dead weight. At 3s + 3s against a 6s liveness window the two coincided
   * exactly, which is how this was originally written — the probe never once
   * decided anything. 3s + 2s = 5s leaves a real margin over the watchdog's
   * 6-8s. Tune either value and re-check this sum.
   *
   * 2s is still a generous budget for the echo: the round trip is POST, Redis
   * publish, SSE write, on a link that was delivering keepalives 3s ago.
   */
  private readonly probeTimeoutMs = 2_000;
  /**
   * Skip the probe while bytes are this recent. Deliberately above
   * serverKeepaliveMs rather than equal to probeIntervalMs: at parity the
   * threshold races the keepalive it is measuring, so `msSinceActivity` sits
   * either side of it at tick time and a healthy stream probes on roughly half
   * its ticks for nothing. A margin of half an interval makes a stream that is
   * actually receiving keepalives quiet, and only a missed one starts probing.
   */
  private readonly probeQuietThresholdMs = 3_000;
  private probeTimer: ReturnType<typeof setInterval> | null = null;
  private probeTimeoutTimer: ReturnType<typeof setTimeout> | null = null;
  /** Nonce of the probe awaiting an echo; null when none is outstanding. */
  private pendingProbeNonce: string | null = null;

  private readonly visibilityHandler = () => this.handleVisibility();
  private readonly onlineHandler = () => this.handleOnline();
  private readonly androidResumeHandler = () =>
    this.ensureLiveOrReconnect(ANDROID_APP_RESUMED_EVENT);
  private readonly androidNetworkHandler = (event: Event) => this.handleAndroidNetworkChange(event);

  constructor() {
    if (typeof document !== 'undefined') {
      document.addEventListener('visibilitychange', this.visibilityHandler);
    }
    if (typeof window !== 'undefined') {
      window.addEventListener('online', this.onlineHandler);
      window.addEventListener(ANDROID_APP_RESUMED_EVENT, this.androidResumeHandler);
      window.addEventListener(ANDROID_NETWORK_CHANGED_EVENT, this.androidNetworkHandler);
    }
    this.startWatchdog();
    this.startProbe();
  }

  ngOnDestroy(): void {
    this.disconnect();
    this.stopWatchdog();
    this.stopProbe();
    if (typeof document !== 'undefined') {
      document.removeEventListener('visibilitychange', this.visibilityHandler);
    }
    if (typeof window !== 'undefined') {
      window.removeEventListener('online', this.onlineHandler);
      window.removeEventListener(ANDROID_APP_RESUMED_EVENT, this.androidResumeHandler);
      window.removeEventListener(ANDROID_NETWORK_CHANGED_EVENT, this.androidNetworkHandler);
    }
    this.eventSubject.complete();
  }

  isConnected(): boolean {
    return this.connected;
  }

  getAuthMode(): RealtimeAuthMode {
    return this.authMode;
  }

  on<T = any>(type: string): Observable<T> {
    return this.events$.pipe(
      filter(e => e.type === type),
      map(e => e.data as T)
    );
  }

  connectUser(): void {
    this.start('user');
  }

  connectTunnel(): void {
    this.start('tunnel');
  }

  reconnect(): void {
    if (this.authMode === 'none') {
      return;
    }
    // Force a new stream even if currently open / waiting for `connected`.
    this.intentionalClose = false;
    this.connected = false;
    this.streamOpen = false;
    this.clearReconnectTimer();
    // The outstanding probe (if any) was addressed to the stream being torn
    // down; its echo can never arrive, and must not fire this timeout again.
    this.clearProbeTimeout();
    this.abort?.abort();
    this.abort = null;
    this.start(this.authMode);
  }

  disconnect(): void {
    this.intentionalClose = true;
    this.clearReconnectTimer();
    this.clearProbeTimeout();
    this.abort?.abort();
    this.abort = null;
    this.connected = false;
    this.streamOpen = false;
    this.authMode = 'none';
    this.reconnectAttempt = 0;
    this.lastActivityAt = 0;
    this.connectionId = null;
    this.hasBeenReady = false;
    this.log('disconnect', 'SSE disconnected');
  }

  private start(mode: RealtimeAuthMode): void {
    if (mode === 'none') {
      return;
    }
    // Avoid tearing down an in-flight handshake / open stream for the same mode.
    if (this.authMode === mode && this.abort && (this.connected || this.streamOpen)) {
      return;
    }
    this.intentionalClose = false;
    this.authMode = mode;
    this.connected = false;
    this.streamOpen = false;
    this.connectionId = null;
    this.clearReconnectTimer();
    this.abort?.abort();
    this.abort = new AbortController();

    const token = this.resolveToken(mode);
    if (!token) {
      this.log('error', `SSE skipped — no ${mode} token`, { mode });
      this.connected = false;
      this.streamOpen = false;
      return;
    }

    void this.runStream(token, this.abort.signal);
  }

  private async runStream(token: string, signal: AbortSignal): Promise<void> {
    const url = new URL(REALTIME_STREAM_URL);
    if (this.lastEventId) {
      url.searchParams.set('lastEventId', this.lastEventId);
    }

    this.log('connecting', 'SSE connecting…', { mode: this.authMode });

    try {
      const response = await fetch(url.toString(), {
        method: 'GET',
        headers: {
          Accept: 'text/event-stream',
          Authorization: `Bearer ${token}`,
          ...(this.lastEventId ? { 'Last-Event-ID': this.lastEventId } : {}),
        },
        signal,
        cache: 'no-store',
      });

      if (!response.ok || !response.body) {
        throw new Error(`SSE HTTP ${response.status}`);
      }

      // HTTP accepted — not ready until server sends `event: connected`.
      this.streamOpen = true;
      this.connected = false;
      this.lastActivityAt = Date.now();
      this.log('open', 'SSE HTTP stream open — waiting for event: connected', {
        mode: this.authMode,
      });

      await this.readLoop(response.body, signal);
      this.connected = false;
      this.streamOpen = false;

      if (!this.intentionalClose && !signal.aborted) {
        this.scheduleReconnect('stream_ended');
      }
    } catch (err) {
      this.connected = false;
      this.streamOpen = false;
      if (this.intentionalClose || signal.aborted) {
        return;
      }
      this.log('error', 'SSE stream error', {
        error: err instanceof Error ? err.message : String(err),
      });
      this.scheduleReconnect('error');
    }
  }

  private async readLoop(body: ReadableStream<Uint8Array>, signal: AbortSignal): Promise<void> {
    const reader = body.getReader();
    const decoder = new TextDecoder();
    let buffer = '';

    while (!signal.aborted) {
      const { done, value } = await reader.read();
      if (done) {
        break;
      }
      // Any byte (keepalive comment or event) proves the socket is alive.
      this.lastActivityAt = Date.now();
      buffer += decoder.decode(value, { stream: true });
      const chunks = buffer.split(/\r?\n\r?\n/);
      buffer = chunks.pop() ?? '';
      for (const chunk of chunks) {
        this.dispatchParsedBlock(chunk);
      }
    }
  }

  private dispatchParsedBlock(block: string): void {
    const trimmed = block.trim();
    if (!trimmed || trimmed.startsWith(':')) {
      return;
    }

    let id: string | undefined;
    let type = 'message';
    const dataLines: string[] = [];

    for (const line of trimmed.split(/\r?\n/)) {
      if (line.startsWith('id:')) {
        id = line.slice(3).trim();
      } else if (line.startsWith('event:')) {
        type = line.slice(6).trim() || type;
      } else if (line.startsWith('data:')) {
        dataLines.push(line.slice(5).trim());
      }
    }

    const dataRaw = dataLines.join('\n');

    if (id) {
      this.lastEventId = id;
    }

    let data: any = dataRaw || null;
    if (dataRaw) {
      try {
        data = JSON.parse(dataRaw);
      } catch {
        // keep string
      }
    }

    let eventType = type;
    let payload: any = data;
    if (data && typeof data === 'object' && !Array.isArray(data)) {
      const obj = data as Record<string, any>;
      if (typeof obj['type'] === 'string' && 'data' in obj) {
        eventType = obj['type'] as string;
        payload = obj['data'];
      } else if (typeof obj['event'] === 'string' && 'data' in obj) {
        eventType = obj['event'] as string;
        payload = obj['data'];
      }
    }

    const eventConnectionId =
      eventType === SSE_CONNECTED_EVENT || type === SSE_CONNECTED_EVENT
        ? this.connectionIdFromPayload(data)
        : this.connectionId;
    this.logSseEvent(eventType, id, eventConnectionId);

    // Control event: stream ready. Not a business notification.
    if (eventType === SSE_CONNECTED_EVENT || type === SSE_CONNECTED_EVENT) {
      this.markStreamReady(data);
      return;
    }

    // Control event: the server enforced the bounded token/session lifetime and
    // is closing the stream. Re-authenticate + reconnect using a FRESH token
    // (resolveToken re-reads localStorage, which the auth layer keeps current).
    if (eventType === SSE_SESSION_EXPIRED_EVENT || type === SSE_SESSION_EXPIRED_EVENT) {
      this.log('reconnecting', 'SSE session expired — re-authenticating', { mode: this.authMode });
      this.connected = false;
      this.streamOpen = false;
      this.connectionId = null;
      this.reconnect();
      return;
    }

    // Control event: our own liveness echo came back on this stream, which is
    // the proof the probe exists to obtain. Not a business notification.
    if (eventType === SSE_PROBE_EVENT || type === SSE_PROBE_EVENT) {
      this.handleProbeEcho(payload ?? data);
      return;
    }

    // Business events require a data payload (pings / empty frames already filtered).
    if (!dataRaw) {
      return;
    }

    // Re-enter Angular zone: the fetch SSE read loop is outside NgZone, so without
    // this, live plate-scan modals open with loading UI that never paints completed APIs.
    this.ngZone.run(() => {
      this.eventSubject.next({ id, type: eventType, data: payload, raw: data });
    });
  }

  private markStreamReady(payload: any): void {
    const wasReady = this.connected;
    // New stream (and possibly a new connection id): any earlier nonce is moot.
    this.clearProbeTimeout();
    this.connected = true;
    this.streamOpen = true;
    this.reconnectAttempt = 0;
    this.lastActivityAt = Date.now();
    const resolvedConnectionId = this.connectionIdFromPayload(payload);
    if (resolvedConnectionId) {
      this.connectionId = resolvedConnectionId;
    }
    if (!wasReady) {
      const reconnect = this.hasBeenReady;
      this.hasBeenReady = true;
      this.log('connected', 'SSE stream ready (event: connected)', {
        mode: this.authMode,
        connectionId: this.connectionId,
        reconnect,
      });
      this.readySubject.next({ mode: this.authMode, connectionId: this.connectionId, reconnect });
    }
  }

  private scheduleReconnect(reason: string): void {
    if (this.intentionalClose || this.authMode === 'none' || this.reconnectTimer) {
      return;
    }
    const base = Math.min(1000 * Math.pow(2, this.reconnectAttempt), this.maxReconnectDelayMs);
    // Full jitter (AWS-style) so many clients don't reconnect in lockstep after
    // a server restart / proxy bounce (thundering herd).
    const delay = Math.round(base / 2 + Math.random() * (base / 2));
    this.reconnectAttempt += 1;
    this.log('reconnecting', `SSE reconnect in ${delay}ms (${reason})`, {
      reason,
      attempt: this.reconnectAttempt,
      delay,
    });
    this.reconnectTimer = setTimeout(() => {
      this.reconnectTimer = null;
      if (!this.intentionalClose && this.authMode !== 'none') {
        this.start(this.authMode);
      }
    }, delay);
  }

  private clearReconnectTimer(): void {
    if (this.reconnectTimer != null) {
      clearTimeout(this.reconnectTimer);
      this.reconnectTimer = null;
    }
  }

  private handleVisibility(): void {
    if (typeof document === 'undefined' || document.visibilityState !== 'visible') {
      return;
    }
    this.ensureLiveOrReconnect('visibilitychange');
  }

  private handleOnline(): void {
    this.ensureLiveOrReconnect('online');
  }

  private handleAndroidNetworkChange(event: Event): void {
    const available = (event as CustomEvent<{ available?: boolean }>).detail?.available;
    // Only the transition back to a usable network is actionable — there is nothing to reconnect
    // to while it is gone, and reconnect backoff already covers the retry.
    if (available === false) {
      return;
    }
    this.ensureLiveOrReconnect(ANDROID_NETWORK_CHANGED_EVENT);
  }

  /**
   * Recover the stream on a resume signal (tab foreground / network back).
   * Critically, this also forces a reconnect when we *think* we're connected
   * but the socket is actually a zombie (no keepalive for `livenessTimeoutMs`):
   * after laptop sleep the old TCP connection is dead yet `connected` is still
   * stale-`true`, so a plain `!connected` guard would never reconnect.
   */
  private ensureLiveOrReconnect(reason: string): void {
    if (this.authMode === 'none' || this.intentionalClose) {
      return;
    }
    // Not connected and not mid-handshake → a reconnect is safe and needed.
    if (!this.connected && !this.streamOpen) {
      this.log('reconnecting', `SSE resume reconnect (${reason})`, { reason });
      this.reconnect();
      return;
    }
    // Looks connected/handshaking but the stream is stale → force a fresh one.
    if (this.isStreamStale()) {
      this.log('reconnecting', `SSE resume — stream stale, forcing reconnect (${reason})`, {
        reason,
        msSinceActivity: this.msSinceActivity(),
      });
      this.reconnect();
    }
  }

  private msSinceActivity(): number {
    return this.lastActivityAt ? Date.now() - this.lastActivityAt : Number.POSITIVE_INFINITY;
  }

  /** A live stream sees a keepalive every serverKeepaliveMs; miss ~3 → treat as dead. */
  private isStreamStale(): boolean {
    if (!this.connected && !this.streamOpen) {
      return false;
    }
    return this.msSinceActivity() > this.livenessTimeoutMs;
  }

  /**
   * Round-trip probe: POST a nonce, expect it back on this stream.
   *
   * Only runs when the stream believes it is up and has been quiet — there is
   * nothing to prove while a reconnect is already pending, and no need to spend
   * a request when events or keepalives are arriving anyway.
   */
  private startProbe(): void {
    if (this.probeTimer != null || typeof setInterval === 'undefined') {
      return;
    }
    this.probeTimer = setInterval(() => this.sendProbe(), this.probeIntervalMs);
  }

  private stopProbe(): void {
    if (this.probeTimer != null) {
      clearInterval(this.probeTimer);
      this.probeTimer = null;
    }
    this.clearProbeTimeout();
  }

  private clearProbeTimeout(): void {
    if (this.probeTimeoutTimer != null) {
      clearTimeout(this.probeTimeoutTimer);
      this.probeTimeoutTimer = null;
    }
    this.pendingProbeNonce = null;
  }

  private createProbeNonce(): string {
    const cryptoRef = typeof crypto !== 'undefined' ? crypto : undefined;
    if (cryptoRef?.randomUUID) {
      return cryptoRef.randomUUID().replace(/-/g, '');
    }
    return `${Date.now().toString(36)}${Math.random().toString(36).slice(2, 10)}`;
  }

  private sendProbe(): void {
    if (this.intentionalClose || this.authMode === 'none' || this.reconnectTimer) {
      return;
    }
    // Nothing to probe unless we believe we have a stream, and no id to address.
    if (!this.connected || !this.connectionId) {
      return;
    }
    // A probe is already outstanding — let its timeout decide.
    if (this.pendingProbeNonce) {
      return;
    }
    // Traffic this recent already proves the stream carries bytes.
    if (this.msSinceActivity() < this.probeQuietThresholdMs) {
      return;
    }

    const token = this.resolveToken(this.authMode);
    if (!token) {
      return;
    }

    const nonce = this.createProbeNonce();
    const connectionId = this.connectionId;
    this.pendingProbeNonce = nonce;
    this.probeTimeoutTimer = setTimeout(() => {
      this.probeTimeoutTimer = null;
      // The echo never arrived on this stream. The POST may well have succeeded
      // — that only ever proved the network and token, never this connection.
      if (this.pendingProbeNonce !== nonce) {
        return;
      }
      this.pendingProbeNonce = null;
      this.log('reconnecting', 'SSE probe timed out — forcing reconnect', {
        connectionId,
        timeoutMs: this.probeTimeoutMs,
        msSinceActivity: this.msSinceActivity(),
      });
      this.reconnect();
    }, this.probeTimeoutMs);

    void fetch(REALTIME_PROBE_URL, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify({ connectionId, nonce }),
      cache: 'no-store',
    })
      .then(response => {
        if (response.ok) {
          return;
        }
        // No echo is coming, but this says nothing about the stream. fetch only
        // rejects on a network failure, so without this a 401 / 429 / 5xx would
        // leave the timeout armed and reconnect a perfectly healthy connection.
        //
        // 429 is the dangerous one: the probe endpoint is rate limited per IP,
        // and a site running several screens behind one NAT address can reach
        // that limit. Reconnecting on it would turn throttling into a permanent
        // reconnect loop — precisely the churn the probe exists to avoid.
        this.abandonProbe(nonce, `http_${response.status}`);
      })
      .catch(err => {
        // Network-level failure. Also not evidence about this stream: the POST
        // travels its own connection, which is the whole reason the echo has to
        // come back down the stream instead.
        this.abandonProbe(nonce, err instanceof Error ? err.message : String(err));
      });
  }

  /**
   * Give up on an outstanding probe without treating it as a dead stream.
   *
   * Disarms the timeout so the next tick can retry. Guarded on the nonce: by the
   * time a response lands the stream may already have reconnected and issued a
   * different probe, and cancelling that one would mask a genuine stall.
   */
  private abandonProbe(nonce: string, reason: string): void {
    if (this.pendingProbeNonce !== nonce) {
      return;
    }
    this.clearProbeTimeout();
    this.log('error', 'SSE probe not delivered — not treating stream as dead', { reason });
  }

  /**
   * Echo received. Only our own outstanding nonce counts: a stale or unknown one
   * says nothing about the current stream.
   */
  private handleProbeEcho(payload: unknown): void {
    const nonce =
      payload && typeof payload === 'object'
        ? String((payload as Record<string, unknown>)['nonce'] ?? '')
        : '';
    if (!nonce || nonce !== this.pendingProbeNonce) {
      return;
    }
    this.clearProbeTimeout();
  }

  private startWatchdog(): void {
    if (this.watchdogTimer != null || typeof setInterval === 'undefined') {
      return;
    }
    this.watchdogTimer = setInterval(() => this.checkLiveness(), this.watchdogIntervalMs);
  }

  private stopWatchdog(): void {
    if (this.watchdogTimer != null) {
      clearInterval(this.watchdogTimer);
      this.watchdogTimer = null;
    }
  }

  /**
   * Periodic liveness check: independent of `online`/`visibilitychange`, so a
   * dead connection is recovered even in a backgrounded tab or a network blip
   * that never emits those events (e.g. proxy idle-timeout reap).
   */
  private checkLiveness(): void {
    if (this.intentionalClose || this.authMode === 'none' || this.reconnectTimer) {
      return;
    }
    if (this.isStreamStale()) {
      this.log('reconnecting', 'SSE liveness watchdog: stream stale, forcing reconnect', {
        msSinceActivity: this.msSinceActivity(),
        livenessTimeoutMs: this.livenessTimeoutMs,
      });
      this.reconnect();
    }
  }

  private resolveToken(mode: RealtimeAuthMode): string | null {
    if (typeof window === 'undefined') {
      return null;
    }
    try {
      if (mode === 'tunnel') {
        const raw = getActiveTunnelDataRaw();
        if (!raw) return null;
        return JSON.parse(raw)?.token ?? null;
      }
      if (mode === 'user') {
        const userRaw = getActiveUserDataRaw();
        if (userRaw) {
          const token = JSON.parse(userRaw)?.token;
          if (token) return token;
        }
        const saRaw = localStorage.getItem('superAdminData');
        if (saRaw) {
          return JSON.parse(saRaw)?.token ?? null;
        }
      }
    } catch {
      return null;
    }
    return null;
  }

  private connectionIdFromPayload(payload: unknown): string | null {
    if (payload && typeof payload === 'object' && !Array.isArray(payload)) {
      const obj = payload as Record<string, unknown>;
      if (typeof obj['connectionId'] === 'string' && obj['connectionId'].trim() !== '') {
        return obj['connectionId'];
      }
    }
    return this.connectionId;
  }

  private logSseEvent(event: string, eventId?: string, connectionId?: string | null): void {
    const payload = {
      connectionId: connectionId ?? this.connectionId,
      event,
      eventId: eventId ?? null,
      mode: this.authMode,
      at: new Date().toISOString(),
    };
    this.logger.trackSseEvent(event, payload);
    console.info(`[SSE][event] ${event}`, payload);
  }

  private log(phase: string, message: string, detail?: Record<string, any>): void {
    const payload = {
      phase,
      message,
      connected: this.connected,
      streamOpen: this.streamOpen,
      mode: this.authMode,
      connectionId: this.connectionId,
      lastEventId: this.lastEventId,
      at: new Date().toISOString(),
      ...detail,
    };
    this.logger.trackSseConnection(`${phase}: ${message}`, payload);
    if (phase === 'error' || phase === 'reconnecting') {
      console.warn(`[SSE][${phase}] ${message}`, payload);
    } else {
      console.info(`[SSE][${phase}] ${message}`, payload);
    }
  }
}
